import { Check } from 'lucide-react';
import { flagTypeById, shortDate, type FlagListItem, type FlagTypeDef } from '../../lib/flags';
import { FlagIcon } from './FlagIcon';

/**
 * Flags as a grouped list (#316): Find, the meeting panel and (phase 2)
 * modules. An addressed flag shows a check and its reply.
 */

export type FlagListGroup = 'kind' | 'source' | 'none' | 'person';

const SOURCE_LABEL: Record<FlagListItem['source_type'], string> = {
  meeting_instance: 'Meeting', task: 'Task', note: 'Note', scratchpad: 'Scratchpad',
};

/** "Meeting - Weekly Ops Sync - Oct 20 - Claims Portal" */
export function sourceLine(i: FlagListItem): string {
  return [SOURCE_LABEL[i.source_type], i.source_title || 'Untitled', shortDate(i.flag_date), ...i.project_names].join(' - ');
}

/** Where a flag's document lives in the app, opening at that flag. */
export function sourceUrl(i: FlagListItem): string {
  const id = encodeURIComponent(i.source_id);
  const path = {
    meeting_instance: i.series_id ? `/meetings/${encodeURIComponent(i.series_id)}/${id}` : `/meetings/standalone/${id}`,
    task: `/tasks/${id}`,
    note: `/notes/${id}`,
    scratchpad: `/scratchpad/${id}`,
  }[i.source_type];
  return `${path}?flag=${encodeURIComponent(i.id)}`;
}

interface Props {
  items: FlagListItem[];
  group: FlagListGroup;
  showSource: boolean;
  types: FlagTypeDef[];
  onOpen: (item: FlagListItem, rect: DOMRect) => void;
  emptyText: string;
}

export function FlagList({ items, group, showSource, types, onOpen, emptyText }: Props) {
  if (items.length === 0) return <p className="px-1.5 py-1.5 text-sm text-text-muted">{emptyText}</p>;

  const groups: Array<{ key: string; label: string; def?: FlagTypeDef; items: FlagListItem[] }> = [];
  if (group === 'none') {
    groups.push({ key: 'all', label: '', items });
  } else {
    const byKey = new Map<string, FlagListItem[]>();
    for (const i of items) {
      const key = group === 'kind' ? i.type : group === 'person' ? (i.person_name ?? 'No one') : `${SOURCE_LABEL[i.source_type]}: ${i.source_title || 'Untitled'}`;
      byKey.set(key, [...(byKey.get(key) ?? []), i]);
    }
    const keys = group === 'kind'
      ? [...types.map((t) => t.id).filter((k) => byKey.has(k)), ...[...byKey.keys()].filter((k) => !types.some((t) => t.id === k))]
      : [...byKey.keys()];
    for (const key of keys) {
      const def = group === 'kind' ? flagTypeById(types, key) : undefined;
      groups.push({ key, label: def ? def.label : key, def, items: byKey.get(key)! });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {groups.map((g) => (
        <section key={g.key} aria-label={g.label || undefined}>
          {g.label && (
            <h4 className="m-0 flex items-center gap-1.5 px-1.5 py-0.5 text-xs font-semibold" style={{ color: g.def?.color ?? 'var(--color-text-secondary)' }}>
              {g.def && <FlagIcon def={g.def} size={13} />}
              {g.label}
              <span className="font-mono text-[11px] font-normal text-text-muted">{g.items.length}</span>
            </h4>
          )}
          {g.items.map((i) => {
            const def = flagTypeById(types, i.type);
            const done = i.status === 'addressed';
            const sub = [i.person_name ? `With ${i.person_name}` : '', showSource ? sourceLine(i) : ''].filter(Boolean).join(' - ');
            return (
              <button key={i.id} type="button" data-flag-item={i.id} onClick={(e) => onOpen(i, e.currentTarget.getBoundingClientRect())}
                className="flex w-full items-start gap-2 rounded-md px-1.5 py-1.5 text-left text-sm hover:bg-bg-hover">
                <span className="mt-0.5"><FlagIcon def={def} size={15} /></span>
                <span className="flex min-w-0 flex-1 flex-col gap-px">
                  <span className={`flag-item-text line-clamp-2 break-words ${done ? 'text-text-secondary' : 'text-text-primary'}`}>
                    {i.text || <i className="text-text-muted">Empty line</i>}
                  </span>
                  {sub && <span className="text-xs text-text-muted">{sub}</span>}
                  {done && i.reply && (
                    <span className="mt-0.5 border-l-2 border-success pl-1.5 text-xs text-text-secondary">
                      <b className="font-semibold text-text-primary">{def.replyLabel ?? 'Note'}:</b> {i.reply}
                    </span>
                  )}
                </span>
                {done && <Check size={14} className="mt-0.5 shrink-0 text-success" aria-label="Addressed" />}
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
}
